import { Injectable } from '@angular/core';
import moment from 'moment';

@Injectable()
export class FormatterService {
  public format(formatterType: string, value): string {
    switch (formatterType) {
      case 'telephone':
        return this.formatPhoneNumber(value as string);
      case 'date_mmyyyy':
        return this.formatShortDate(value as string);
      case 'date_mdyyyy':
        return this.formatUTCDate(value as Date, false);
      case 'date_mmddyyyy':
        return this.formatUTCDate(value as Date);
      case 'currency':
        return this.formatCurrency(value as number);
      case 'currencyWithCents':
        return this.formatCurrencyWithCents(value as number);
      case 'zip':
        return this.formatZip(value as string);
      case 'ssn':
        return this.formatSSN(value as string);
      case 'ein':
        return this.formatEIN(value as string);
      case 'email':
        return this.formatEmail(value as string);
      default:
        return this.formatString(value as object);
    }
  }

  public deriveFormatterTypeFromRule(rule: string): string {
    let formatterType = null;

    switch (rule) {
      case 'currency':
      case 'currencyZero':
      case 'currencyRounded':
      case 'currencyRoundedConditionally':
        formatterType = 'currency';
        break;
      case 'currencyWithCents':
        formatterType = 'currencyWithCents';
        break;
      case 'DOB':
      case 'date':
      case 'futuredate':
        formatterType = 'date_mmddyyyy';
        break;
      case 'shortdate':
        formatterType = 'date_mmyyyy';
        break;
      case 'numeric':
      case 'numericPositive':
        // formatterType = 'number';
        break;
      case 'telephone':
        formatterType = 'telephone';
        break;
      case 'zipcode':
        formatterType = 'zip';
        break;
      case 'ssn':
        formatterType = 'ssn';
        break;
      case 'ein':
        formatterType = 'ein';
      default:
        break;
    }

    return formatterType;
  }

  /**
   * Format a date to be m/d/yyy or mm/dd/yyyy in UTC time zone
   * @param value the date to be formatted
   * @param withZeros if true, format to mm/dd/yyyy with leading zeros in month and days. E.g., 08/01/2020
   * Otherwise, format to m/d/yyyy without leading zeros in month and days. E.g., 8/1/2020
   */
  public formatUTCDate(value: Date, withZeros: boolean = true): string {
    if (!value) {
      return '';
    }

    const date = value;
    const utcMonthNumber = date.getUTCMonth() + 1;
    const utcMonth = utcMonthNumber < 10 && withZeros ? '0' + utcMonthNumber : utcMonthNumber;
    const utcDay = date.getUTCDate() < 10 && withZeros ? '0' + date.getUTCDate() : date.getUTCDate();

    return utcMonth + '/' + utcDay + '/' + date.getUTCFullYear();
  }

  // If the value: Date passed in is in EST, then this function just simply format the EST time into M/D/YYYY
  public formatLocalDateNoZeros(value: Date): string {
    if (!value) {
      return '';
    }

    return moment(value).format('M/D/YYYY');
  }

  /** Formatters - make private so we can simplify the usage of FormatterService **/

  private formatEIN(value: string): string {
    if (!value) {
      return '';
    }

    const strippedValue = value.replace(/[^0-9]/g, '');

    if (strippedValue.length === 9) {
      const einBuffer = [];

      for (let i = 0; i < strippedValue.length; i++) {
        switch (i) {
          case 2:
            einBuffer.push('-');
            einBuffer.push(strippedValue[i]);
            break;
          default:
            einBuffer.push(strippedValue[i]);
            break;
        }
      }
      return einBuffer.join('');
    } else {
      return value;
    }
  }

  private formatSSN(value: string): string {
    if (!value) {
      return '';
    }

    const strippedValue = value.replace(/[^0-9]/g, '');

    if (strippedValue.length === 9) {
      const ssnBuffer = [];

      for (let i = 0; i < strippedValue.length; i++) {
        switch (i) {
          case 3:
            ssnBuffer.push('-');
            ssnBuffer.push(strippedValue[i]);
            break;
          case 5:
            ssnBuffer.push('-');
            ssnBuffer.push(strippedValue[i]);
            break;
          default:
            ssnBuffer.push(strippedValue[i]);
            break;
        }
      }
      return ssnBuffer.join('');
    } else {
      return value;
    }
  }

  private formatShortDate(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);

    if (!date.valueOf()) {
      return value;
    } else {
      const utcMonth = date.getUTCMonth() + 1 < 10 ? '0' + (date.getUTCMonth() + 1) : date.getUTCMonth() + 1;

      return utcMonth + '/' + date.getUTCFullYear();
    }
  }

  private formatZip(value: string): string {
    if (!value) {
      return '';
    }

    const strippedValue = value.replace(/[^0-9]/g, '');

    if (strippedValue.length === 5 || strippedValue.length === 9) {
      const zipBuffer = [];
      for (let i = 0; i < strippedValue.length; i++) {
        switch (i) {
          case 5:
            zipBuffer.push('-');
            zipBuffer.push(strippedValue[i]);
            break;
          default:
            zipBuffer.push(strippedValue[i]);
            break;
        }
      }
      return zipBuffer.join('');
    } else {
      return value;
    }
  }

  private formatPhoneNumber(value: string): string {
    const phoneBuffer = [];

    if (!value) {
      return '';
    }

    const strippedValue = value.replace(/[^0-9]/g, '');
    const numberLength = strippedValue.length;

    if (strippedValue.length < 10) {
      return value;
    }

    for (let i = 0; i < numberLength; i++) {
      switch (i) {
        case 0:
          phoneBuffer.push('(');
          break;
        case 3:
          phoneBuffer.push(') ');
          break;
        case 6:
          phoneBuffer.push('-');
          break;
        case 10:
          phoneBuffer.push(' x');
          break;
        default:
      }
      phoneBuffer.push(strippedValue.charAt(i));
    }

    return phoneBuffer.join('');
  }

  private formatCurrency(value: number): string {
    if (value === null) {
      return '';
    }

    const strippedValue = value.toString().replace(/[^-0-9.]/g, '');

    return this.formatCurrencyInternal(strippedValue, '$', false);
  }

  private formatCurrencyWithCents(value: number): string {
    if (value === null) {
      return '';
    }

    const strippedValue = value.toString().replace(/[^-0-9.]/g, '');

    return this.formatCurrencyInternal(strippedValue, '$', true);
  }

  private formatCurrencyInternal(value, prependSymbol, noround) {
    prependSymbol = prependSymbol || '';

    let n = String(noround ? value : Math.round(value));
    n = n.replace(/\.([0-9])$/, '.$10');

    const p = value < 0 ? ['-', ''] : ['', ''];

    return p[0] + prependSymbol + this.addCommas(n.replace(/-/, '')) + p[1];
  }

  private addCommas(nStr) {
    nStr = String(nStr);
    const x = nStr.split('.');
    let x1 = x[0];
    const x2 = x.length > 1 ? '.' + x[1] : '';
    const rgx = /(\d+)(\d{3})/;

    while (rgx.test(x1)) {
      x1 = x1.replace(rgx, '$1' + ',' + '$2');
    }

    return x1 + x2;
  }

  private formatEmail(value: string): string {
    return value ? value.toLowerCase().trim() : '';
  }

  private formatString(value) {
    if (!value) {
      return '';
    }

    return value.toString();
  }
}
